/**
 * useImageUpload - Hook for uploading images to R2
 *
 * This hook provides a simple interface for uploading images to the backend.
 * Images are stored in R2 with tenant isolation.
 *
 * Usage:
 * ```tsx
 * const { upload, isUploading, error, progress } = useImageUpload();
 *
 * const handleFileSelect = async (file: File) => {
 *   const result = await upload(file);
 *   if (result) {
 *     // Use result.proxyUrl in ImageNode
 *     createImageNode(result.proxyUrl, file.name);
 *   }
 * };
 * ```
 *
 * How it works:
 * 1. Create FormData with the file
 * 2. POST to /api/upload/image
 * 3. Backend validates and uploads to R2
 * 4. Backend returns proxy URL
 * 5. Use proxy URL in ImageNode src
 *
 * The proxy URL format is: /api/upload/image/{filename}
 * This URL is authenticated via session cookie.
 */
import { useState, useCallback } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * UploadResult - Response from successful upload
 *
 * @property success - Always true for successful uploads
 * @property key - Full R2 key (tenants/{tenantId}/images/{filename})
 * @property proxyUrl - URL to use in ImageNode (/api/upload/image/{filename})
 */
interface UploadResult {
  success: boolean;
  /** Full R2 key including tenant path */
  key: string;
  /** Proxy URL for serving the image */
  proxyUrl: string;
}

/**
 * UseImageUploadReturn - Return type of useImageUpload hook
 *
 * @property upload - Function to upload a file
 * @property isUploading - True while upload is in progress
 * @property error - Error message if upload failed, null otherwise
 * @property progress - Upload progress 0-100 (simplified, not real progress)
 */
interface UseImageUploadReturn {
  /** Upload a file and return the result */
  upload: (file: File) => Promise<UploadResult | null>;
  /** True while upload is in progress */
  isUploading: boolean;
  /** Error message if upload failed */
  error: string | null;
  /** Simplified progress indicator (0, 30, 70, 100) */
  progress: number;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * useImageUpload - React hook for image uploads
 *
 * State management:
 * - isUploading: tracks if upload is in progress
 * - error: stores error message if upload fails
 * - progress: simplified progress (not real upload progress)
 *
 * Progress values:
 * - 0: Not started
 * - 30: Request started
 * - 70: Response received
 * - 100: Complete
 *
 * Note: Real upload progress would require XMLHttpRequest or fetch with
 * ReadableStream, which adds complexity. For small images, the simplified
 * progress is usually sufficient.
 */
export function useImageUpload(): UseImageUploadReturn {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // ---------------------------------------------------------------------------
  // UPLOAD FUNCTION
  // ---------------------------------------------------------------------------
  /**
   * Upload a file to R2 via the backend
   *
   * @param file - The File object to upload
   * @returns UploadResult on success, null on failure
   *
   * The function:
   * 1. Creates FormData with the file
   * 2. POSTs to /api/upload/image
   * 3. Handles success/error responses
   * 4. Returns the result or null
   */
  const upload = useCallback(async (file: File): Promise<UploadResult | null> => {
    // Reset state
    setIsUploading(true);
    setError(null);
    setProgress(0);

    try {
      // Create FormData with file
      const formData = new FormData();
      formData.append('file', file);

      setProgress(30); // Started

      // Upload to backend
      // credentials: 'include' sends the session cookie
      const response = await fetch('/api/upload/image', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      setProgress(70); // Response received

      // Handle non-OK responses
      if (!response.ok) {
        // Try to parse error message from response
        let errorMessage = 'Upload failed';
        try {
          const data = await response.json();
          errorMessage = data.error || errorMessage;
        } catch {
          // Response wasn't JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Parse successful response
      const result: UploadResult = await response.json();
      setProgress(100); // Complete

      console.log('[useImageUpload] Upload successful:', result.proxyUrl);
      return result;
    } catch (err) {
      // Extract error message
      const message = err instanceof Error ? err.message : 'Upload failed';
      console.error('[useImageUpload] Upload error:', message);
      setError(message);
      return null;
    } finally {
      // Always clear uploading state
      setIsUploading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // RETURN
  // ---------------------------------------------------------------------------
  return {
    upload,
    isUploading,
    error,
    progress,
  };
}
